import { useEffect, useRef } from 'react';
import type { LevelIdentity } from '@contracts';
import { RewardIcon } from '@ui/shared/icons/RewardIcon';

/**
 * Level-up moment (badges-and-celebrations.md SS5) — the big celebration.
 * Full-screen scrim, silver (never category-colored: levels aggregate all
 * categories, so luminance-not-hue is the celebration per SS5's closing
 * note). Renders the level's ratified name + icon, never a bare numeral
 * (settings-rewards.md SS1.2's ratified arc). Persists until dismissed —
 * button, Escape, or any tap after the 1600ms entrance completes early-skips
 * straight to rest state (spec: "before that, taps skip to rest state").
 *
 * Reduced motion: static panel (ring, numeral, name, icon, copy) — this
 * component renders the same DOM either way and relies on tokens.css's
 * global `prefers-reduced-motion` rule to collapse the CSS transition
 * durations; there is no particle system here to separately switch off
 * (§5's particles are a visual flourish this build renders as a static
 * glow rather than an animated particle field — see the glow div below).
 */
export function LevelUpMoment({
  level,
  identity,
  xpTotal,
  onDismiss,
}: {
  readonly level: number;
  readonly identity: LevelIdentity;
  readonly xpTotal: number;
  readonly onDismiss: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    panelRef.current?.focus();
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onDismiss();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onDismiss]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Level ${level} reached, ${identity.name}`}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-5)',
        background: 'color-mix(in srgb, var(--surface-base) 80%, transparent)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        padding: 'var(--space-6)',
        textAlign: 'center',
      }}
      onClick={onDismiss}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-4)' }}
      >
        <div
          aria-hidden="true"
          style={{
            position: 'relative',
            width: '160px',
            height: '160px',
            borderRadius: 'var(--radius-pill)',
            border: '4px solid var(--text-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 0 48px color-mix(in srgb, var(--text-primary) 30%, transparent)',
          }}
        >
          <RewardIcon iconId={identity.iconId} size={56} color="var(--text-primary)" />
        </div>

        <div
          className="num"
          style={{
            fontSize: 'calc(var(--type-display-size) * 2.4)',
            lineHeight: 1,
            fontWeight: 'var(--type-display-weight)',
            color: 'var(--text-primary)',
          }}
        >
          {level}
        </div>

        <h2
          style={{
            margin: 0,
            fontSize: 'var(--type-heading-size)',
            fontWeight: 'var(--type-heading-weight)',
            color: 'var(--text-primary)',
          }}
        >
          {identity.name}
        </h2>

        <p className="num" style={{ margin: 0, fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
          {xpTotal.toLocaleString()} XP lifetime
        </p>

        <button
          type="button"
          onClick={onDismiss}
          style={{
            marginTop: 'var(--space-2)',
            minHeight: 'var(--touch-target-min)',
            padding: '0 var(--space-6)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--hue-primary-vivid)',
            color: 'var(--text-on-accent)',
            fontWeight: 600,
            fontSize: 'var(--type-body-size)',
            border: '1px solid var(--hue-primary-strong)',
          }}
        >
          Keep going
        </button>
      </div>
    </div>
  );
}
